import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, LoadingOverlay, Modal, Stack, Text, Title, Tooltip } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconFileDownload } from "@tabler/icons-react";
import dayjs from "dayjs";
import { ExportCampaignResultsParamsInput, ExportJob } from "src/graphql/generated/graphql";
import { checkExportJobState } from "src/utils/helpers";

import { createColumnHelper, DataGrid } from "../../components/DataGrid";
import { useCampaignResultsExportJobs } from "../../hooks/campaignResultsExportJobs";
import { useExportCampaignResults } from "../../hooks/exportCampaignResults";
import { createModal } from "../../hooks/modals";
import { useDownloadExportJob } from "../../hooks/system";

const columnHelper = createColumnHelper<ExportJob>();

export interface ExportCampaignResultsModalProps {
    campaignId: string;
    onClose();
}

export const ExportCampaignResultsModal = createModal(({ campaignId, onClose }: ExportCampaignResultsModalProps) => {
    const { t } = useTranslation(["common"]);

    const { exportJobs, isLoadingExportJobs, reloadExportJobs } = useCampaignResultsExportJobs(campaignId);

    const columns = useMemo(
        () => [
            columnHelper.accessor("startedAt", {
                id: "startedat",
                header: t("date"),
                cell: (props) =>
                    props?.getValue() ? dayjs(props.getValue()).format("DD.MM.YYYY") : <Text color="dimmed">-</Text>,
                footer: (props) => props.column.id,
            }),
            columnHelper.accessor("state", {
                id: "state",
                header: t("exportjob_state"),
                cell: (props) => {
                    const value = props?.getValue();
                    return value && checkExportJobState(value) ? t(`exportjob_state_${value}`) : <Text color="dimmed">-</Text>;
                },
                footer: (props) => props.column.id,
            }),
            columnHelper.display({
                id: "actions",
                header: "",
                cell: (props) => (
                    <Tooltip label={t("exportjob_download")} withArrow>
                        <Button
                            variant="subtle"
                            color="orange"
                            onClick={() => handleDownloadExport(props?.row?.original?.id)}
                            leftIcon={<IconFileDownload size="1em" />}
                            compact
                            px="xs"
                        >
                            {t("download_title")}
                        </Button>
                    </Tooltip>
                ),
                footer: (props) => props.column.id,
            }),

            /* columnHelper.accessor("stats", {// TODO  currently backend return 0 bytes anyway
                id: "size",
                header: t("exportjob_size"),
                cell: (props) => {
                    const stats = props?.getValue();

                    if (!stats?.compressedBytes && !stats?.originalBytes) {
                        return <Text color="dimmed">-</Text>;
                    }

                    return (
                        <Text>
                            <Text weight={500} span>
                                {bytesToSize(stats.compressedBytes)}
                            </Text>{" "}
              (<Text span>{bytesToSize(stats.originalBytes)}</Text>)
                        </Text>
                    );
                },
                footer: (props) => props.column.id,
            }),*/
            // eslint-disable-next-line react-hooks/exhaustive-deps
        ],
        [t]
    );

    const paramsForm = useForm<ExportCampaignResultsParamsInput>({
        initialValues: {
            includeImages: true,
            includeVideos: false,
            includeStamps: false,
        },
    });

    const { exportCampaignResults, isExportingCampaignResults, jobId } = useExportCampaignResults(campaignId);

    useEffect(() => {
        if (jobId) {
            reloadExportJobs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jobId]);

    const handleSubmit = useCallback(async (params: ExportCampaignResultsParamsInput) => {
        const downloadUrl = await exportCampaignResults(params);
        reloadExportJobs();

        if (downloadUrl) {
            window.open(downloadUrl, "_blank");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { downloadExportJob, isDownloadingExportJob } = useDownloadExportJob();

    const handleDownloadExport = useCallback(
        async (jobId: string) => {
            const downloadUrl = await downloadExportJob(jobId);

            if (!downloadUrl) {
                return;
            }

            window.open(downloadUrl, "_blank");
        },
        [downloadExportJob]
    );

    return (
        <Modal size="lg" title={<Title order={2}>{t("export_title")}</Title>} onClose={onClose} opened>
            <LoadingOverlay visible={isLoadingExportJobs || isDownloadingExportJob} />
            <Box component="form" onSubmit={paramsForm.onSubmit(handleSubmit)}>
                <Stack mb="xl" spacing="md">
                    <Button type="submit" loading={isExportingCampaignResults}>
                        {t("campaign_images_export_all_photos")}
                    </Button>
                </Stack>
            </Box>
            <DataGrid
                data={exportJobs.sort((logA, logB) => logB.startedAt - logA.startedAt)}
                columns={columns}
                isLoading={isLoadingExportJobs}
                getRowId={(record) => record?.id}
                mobileHeaderIndex={0}
                secMobileHeaderIndex={2}
            />
        </Modal>
    );
});
